import { computed, Injectable, signal } from '@angular/core';
import { CheckStatus } from '../../../core/models/checks-model';

export interface SelfCheckItem {
  id: number;
  label: string;
  value: boolean;
  disabled: boolean;
}

/**
 * CheckStepperService
 *
 * Steuert den Check-Prozess und die Übergänge zwischen Check-Status.
 *
 * Verantwortlichkeiten:
 * - Verwaltung der Self-Check Checkliste
 * - Steuerung des Check-Prozessstatus (Kontrolle 1-1 → 1-2 → 2 → Abgeschlossen)
 * - Tracking ob der aktuelle User ein Gutachter ist
 * - State für Check-Übergänge (selfCheckDone, checkState)
 */
@Injectable({
  providedIn: 'root',
})
export class CheckStepperService {
  // Self-Check Checkliste
  readonly selfChecks = signal<SelfCheckItem[]>([
    { id: 1, label: 'Sind die Dokumente vollständig?', value: false, disabled: false },
    { id: 2, label: 'Sind die Formvorlagen berücksichtigt?', value: false, disabled: false },
    { id: 3, label: 'Sind die Dokumente fachlich richtig?', value: false, disabled: false },
    { id: 4, label: 'Gutachterliche Tätigkeit', value: false, disabled: false },
  ]);

  readonly checkerIsConsultant = computed(() =>
    this.selfChecks().find(c => c.id === 4)?.value ?? false
  );

  readonly allSelfChecksCompleted = computed(() =>
    this.selfChecks()
      .filter(c => !c.disabled)
      .every(c => c.value)
  );

  readonly selfCheckDone = signal<boolean>(false);

  // Check State - true = checker muss arbeiten, false = actor muss arbeiten
  readonly checkState = signal<boolean>(false);

  updateSelfCheck(id: number, value: boolean): void {
    this.selfChecks.update(checks =>
      checks.map(c => (c.id === id ? { ...c, value } : c))
    );
  }

  toggleSelfCheck(id: number): void {
    this.selfChecks.update(checks =>
      checks.map(c => (c.id === id ? { ...c, value: !c.value } : c))
    );
  }

  disableSelfCheck(id: number, disabled: boolean): void {
    this.selfChecks.update(checks =>
      checks.map(c => (c.id === id ? { ...c, disabled } : c))
    );
  }

  resetSelfChecks(): void {
    this.selfChecks.update(checks =>
      checks.map(c => ({ ...c, value: false, disabled: false }))
    );
    this.selfCheckDone.set(false);
  }

  setSelfCheckDone(done: boolean): void {
    this.selfCheckDone.set(done);
  }

  setCheckState(state: boolean): void {
    this.checkState.set(state);
  }

  /**
   * Berechnet den nächsten Check-Status basierend auf dem aktuellen Status.
   * Bei gutachterlicher Tätigkeit (isConsultant) wird Kontrolle 1-2 übersprungen.
   */
  getNextStatus(currentStatus: CheckStatus, isConsultant = false): CheckStatus {
    switch (currentStatus) {
      case CheckStatus['Kontrolle 1-1']:
        return isConsultant
          ? CheckStatus['Kontrolle 2']
          : CheckStatus['Kontrolle 1-2'];
      case CheckStatus['Kontrolle 1-2']:
        return CheckStatus['Kontrolle 2'];
      case CheckStatus['Kontrolle 2']:
        return CheckStatus.Abgeschlossen;
      default:
        return currentStatus;
    }
  }

  /**
   * Prüft ob der Check in den nächsten Status übergehen kann.
   */
  canAdvanceToNextStatus(currentStatus: CheckStatus, commentsResolved: boolean): boolean {
    // Für den Übergang von "Kontrolle 1-1" zu "Kontrolle 1-2" müssen Self-Checks erledigt sein
    if (currentStatus === CheckStatus['Kontrolle 1-1']) {
      return this.allSelfChecksCompleted() && this.selfCheckDone();
    }

    // Für alle anderen Übergänge müssen Kommentare resolved sein
    return commentsResolved;
  }

  resetAll(): void {
    this.resetSelfChecks();
    this.checkState.set(false);
  }
}

